import * as React from "react";
import { Alert, Box, ToggleButton, ToggleButtonGroup } from "@mui/material";
import RaterManagementPanel from "../../../registrations/registrationDetail/raterManagement/raterManagementPanel";
import { SessionDetailType } from "../../../../services/types/sessionDetailTypes";
import {
    eGroupIds,
    UserClaimsService,
} from "../../../../services/currentUserService/currentUserService";
import { DetailsTabPanel } from "../../../../components/common/detailTabPanel/detailTabPanel";
import PlatformDatesAccordion from "../../../../components/shared/platformsDatesAccordion";
import { useAreaPreference } from "../../../../hooks/useAreaPreference";
import ScoringStatusPanel from "./scoringStatus/scoringStatusPanel";

interface AssessmentsPanelProps {
    sessionDetails: SessionDetailType | undefined;
    handleJournalEntry: (keys: string[]) => void;
    showSendParticipantsEmail?: boolean;
    onSendParticipantsEmail?: (esiKeys: number[]) => void;
}

const AssessmentsPanel: React.FC<AssessmentsPanelProps> = (props) => {
    const [activeTab, setActiveTab] = useAreaPreference<number>(
        "sessionDetail",
        "assessments.subTab",
        0,
    );
    const [hideAboutMe, setHideAboutMe] = useAreaPreference<boolean | null>(
        "sessionDetail",
        "assessments.hideAboutMe",
        null,
    );
    const [keyDatesExpanded, setKeyDatesExpanded] = useAreaPreference<boolean>(
        "sessionDetail",
        "assessments.keyDatesExpanded",
        true,
    );
    const [hidePlatformDates, setHidePlatformDates] = React.useState<boolean>(false);
    const [hideRaterManagement, setHideRaterManagement] = React.useState<boolean>(false);

    const updateHidePlatformDates = (value: boolean) => {
        setHidePlatformDates(value);
    };

    const updateHideRaterManagement = (value: boolean) => {
        setHideRaterManagement(value);
    };

    const claimsService = new UserClaimsService();

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number | null) => {
        if (newValue != null) setActiveTab(newValue);
    };

    const isProductSale =
        props.sessionDetails?.typeKey === 7 || props.sessionDetails?.typeKey === 9;

    let tablist: [string, number, React.ReactNode][] = [
        [
            "Assessment Status",
            0,
            <ScoringStatusPanel
                sessionKey={props.sessionDetails?.sessionKey}
                sessionCode={props.sessionDetails?.sessionCode}
                handleJournalEntry={props.handleJournalEntry}
                source="session"
                sessionDetails={props.sessionDetails}
                participant={undefined}
                hostPlatformKey={props.sessionDetails?.hostingPlatformKey ?? 0}
                surveyPlatformKey={props.sessionDetails?.assessmentPlatformKey ?? 0}
                startDate={props.sessionDetails?.start ?? null}
                isProductSale={isProductSale}
                updateHidePlatformDates={updateHidePlatformDates}
                updateHideRaterManagement={updateHideRaterManagement}
                hideAboutMe={hideAboutMe}
                onAboutMeVisibilityChange={setHideAboutMe}
                showSendParticipantsEmail={props.showSendParticipantsEmail}
                onSendParticipantsEmail={props.onSendParticipantsEmail}
            />,
        ],
        [
            "Rater Information",
            1,
            <RaterManagementPanel
                sessionKey={props.sessionDetails?.sessionKey ?? 0}
                programCode={props.sessionDetails?.sessionCode ?? ""}
                registration={undefined}
                readOnly={!claimsService.IsUserInAppRole(3)}
                updateHidePlatformDates={updateHidePlatformDates}
                hideRaterManagement={hideRaterManagement}
                hostPlatformKey={props.sessionDetails?.hostingPlatformKey ?? 0}
                surveyPlatformKey={props.sessionDetails?.assessmentPlatformKey ?? 0}
            />,
        ],
    ];

    if (claimsService.IsUserInGroups([eGroupIds.eClientAdmin])) {
        tablist = tablist.filter((tab) => tab[0] !== "Rater Information");
    }

    if (props.sessionDetails === undefined) {
        return (
            <Box>
                <Alert severity="error">Error: Session data not found!</Alert>
            </Box>
        );
    }

    return (
        <>
            <Box
                sx={{
                    width: 1,
                    height: 1,
                    m: 2,
                    marginTop: 2,
                    borderWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <Box
                    display={"flex"}
                    justifyContent={"center"}
                    sx={{ borderWidth: 0, flexGrow: 1 }}
                >
                    {tablist.length > 1 ? (
                        <ToggleButtonGroup
                            color="primary"
                            value={activeTab}
                            exclusive
                            onChange={handleTabChange}
                            sx={{ mb: 3 }}
                        >
                            {tablist.map((tab, i) => (
                                <ToggleButton
                                    key={i}
                                    id={`session-details-assessments-tab-${i}`}
                                    value={i}
                                >
                                    {tab[0]}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    ) : null}
                </Box>
                {!hidePlatformDates && (
                    <Box sx={{ padding: 2 }}>
                        <PlatformDatesAccordion
                            sessionDetails={props.sessionDetails}
                            readonly={false}
                            fromMyAccess={false}
                            keyDatesExpanded={keyDatesExpanded}
                            onKeyDatesExpandedChange={setKeyDatesExpanded}
                        />
                    </Box>
                )}
                {tablist.map((tab, i) => (
                    <DetailsTabPanel
                        detailType={"session-details-assessments"}
                        key={tab[0]}
                        value={activeTab}
                        index={i}
                    >
                        {tab[2]}
                    </DetailsTabPanel>
                ))}
            </Box>
        </>
    );
};

export default AssessmentsPanel;
